import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import mainService from '../services/mainService';
import { useToast } from '../contexts/ToastContext';
import './Auth.css';

const Register = () => {
  const { showToast } = useToast();
  const [formData, setFormData] = useState({
    Name: '',
    MobileNo: '',
    Password: '',
    CPassword: '',
    EmailId: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Check password strength
    if (name === 'Password') {
      let strength = 0;
      if (value.length >= 8) strength++;
      if (/[A-Z]/.test(value)) strength++;
      if (/[a-z]/.test(value)) strength++;
      if (/[0-9]/.test(value)) strength++;
      if (/[^A-Za-z0-9]/.test(value)) strength++;
      setPasswordStrength(strength);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      // Call the registerCustomer API
      await mainService.registerCustomer(formData);
      
      setIsLoading(false);
      showToast('Registration successful! Please check your mobile for verification.', 'success');
      
      // Reset form after successful registration
      setFormData({
        Name: '',
        MobileNo: '',
        Password: '',
        CPassword: '',
        EmailId: ''
      });
    } catch (error) {
      console.error('Error during registration:', error);
      setIsLoading(false);
      showToast('Registration failed. Please try again.', 'error');
    }
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength === 0) return '';
    if (passwordStrength <= 2) return 'Weak';
    if (passwordStrength <= 3) return 'Fair';
    if (passwordStrength <= 4) return 'Good';
    return 'Strong';
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength <= 2) return '#cb877f';
    if (passwordStrength <= 3) return '#d5b96f';
    if (passwordStrength <= 4) return '#4CAF50';
    return '#2E7D32';
  };

  return (
    <div className="auth-container">
      <div className="auth-content">
        {/* Left Side - Informational */}
        <div className="auth-info">
          <div className="auth-info-content">
            <div className="auth-logo">
              <img src="/images/logo.png" alt="Eilir" className="logo-image" />
            </div>
            
            <h2 className="auth-title">Join Our Community!</h2>
            <p className="auth-subtitle">
              Create your account and start your shopping journey with exclusive benefits
            </p>
            
            <div className="auth-features">
              <div className="feature-item">
                <span className="feature-icon">🎁</span>
                <div className="feature-text">
                  <h4>Exclusive Offers</h4>
                  <p>Get access to member-only discounts</p>
                </div>
              </div>
              
              <div className="feature-item">
                <span className="feature-icon">⭐</span>
                <div className="feature-text">
                  <h4>Rewards Program</h4>
                  <p>Earn points on every purchase</p>
                </div>
              </div>
              
              <div className="feature-item">
                <span className="feature-icon">🔔</span>
                <div className="feature-text">
                  <h4>Early Access</h4>
                  <p>Be the first to know about new products</p>
                </div>
              </div>
            </div>
            
            <div className="auth-footer">
              <p>Already have an account? <Link to="/login" className="auth-link">Sign in here</Link></p>
            </div>
          </div>
        </div>

        {/* Right Side - Registration Form */}
        <div className="auth-form-container">
          <div className="auth-form-card">
            <div className="form-header">
              <h2>Create Account</h2>
              <p>Fill in your details to create your account</p>
            </div>

            <form onSubmit={handleSubmit} className="auth-form">
              <div className="form-group">
                <label htmlFor="Name">Full Name</label>
                <input
                  type="text"
                  id="Name"
                  name="Name"
                  value={formData.Name}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="Enter your full name"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="MobileNo">Mobile Number</label>
                <input
                  type="tel"
                  id="MobileNo"
                  name="MobileNo"
                  value={formData.MobileNo}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="Enter your mobile number"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="EmailId">Email Address</label>
                <input
                  type="email"
                  id="EmailId"
                  name="EmailId"
                  value={formData.EmailId}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="Enter your email address"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="Password">Password</label>
                <input
                  type="password"
                  id="Password"
                  name="Password"
                  value={formData.Password}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="Create a strong password"
                  required
                />
                {formData.Password && (
                  <div className="password-strength">
                    <div className="strength-bars">
                      {[1, 2, 3, 4, 5].map((bar) => (
                        <div
                          key={bar}
                          className={`strength-bar ${bar <= passwordStrength ? 'active' : ''}`}
                          style={{
                            backgroundColor: bar <= passwordStrength ? getPasswordStrengthColor() : '#333'
                          }}
                        ></div>
                      ))}
                    </div>
                    <span className="strength-text" style={{ color: getPasswordStrengthColor() }}>
                      {getPasswordStrengthText()}
                    </span>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="CPassword">Confirm Password</label>
                <input
                  type="password"
                  id="CPassword"
                  name="CPassword"
                  value={formData.CPassword}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="Confirm your password"
                  required
                />
                {formData.CPassword && formData.Password !== formData.CPassword && (
                  <span className="error-text">Passwords do not match</span>
                )}
              </div>

              <button 
                type="submit" 
                className={`auth-btn auth-btn-create ${isLoading ? 'loading' : ''}`}
                disabled={isLoading || formData.Password !== formData.CPassword}
              >
                {isLoading ? (
                  <>
                    <span className="spinner"></span>
                    Creating account...
                  </>
                ) : (
                  'Create Account'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
